<template>
    <b-card class="console-section mb-3" no-body>
        <div class="section-head d-flex align-items-center justify-content-between px-3 py-2">
            <div>
                <span class="section-index">②</span>
                <span class="fw-500 ml-1">배정 규칙</span>
                <span class="text-secondary small ml-2">답변에 따라 배정될 평가지 ({{ countLabel }})</span>
                <span v-if="truncated" class="text-warning small ml-1">
                    — 일부만 표시, 전체는 고급 화면에서 확인
                </span>
            </div>
            <b-button size="sm" variant="primary" class="flex-shrink-0" @click="$emit('add')">
                규칙 추가
            </b-button>
        </div>

        <div class="p-3">
            <p-page-empty-component
                v-if="ruleSets.length === 0"
                text="아직 만든 배정 규칙이 없습니다. 규칙이 없으면 모든 신청자가 기본 평가지로 배정됩니다."
            />

            <b-row v-else>
                <b-col
                    v-for="card in cards"
                    :key="card.id"
                    cols="12"
                    md="6"
                    class="mb-3"
                >
                    <div class="rule-card h-100" :class="{ 'rule-card--off': !card.is_active }">
                        <div class="d-flex align-items-start justify-content-between mb-2">
                            <div class="fw-500">
                                {{ card.campaignName || `평가지 #${card.campaign_id}` }}
                            </div>
                            <b-badge :variant="card.is_active ? 'success' : 'secondary'" class="flex-shrink-0">
                                {{ card.is_active ? '켜짐' : '꺼짐' }}
                            </b-badge>
                        </div>

                        <!-- 상세(조건 트리) 조회 실패: 네트워크 오류를 '조건 없음'으로 위장하지 않는다 -->
                        <div v-if="card.detailError" class="text-danger small mb-2">
                            <i class="bi bi-exclamation-triangle-fill mr-1"></i>배정 조건을 불러오지 못했습니다.
                            <b-button size="sm" variant="link" class="text-danger p-0 align-baseline" @click="$emit('edit', card.raw)">
                                고급에서 확인
                            </b-button>
                        </div>
                        <template v-else>
                            <div class="text-secondary small mb-1">이럴 때 배정:</div>
                            <ul class="rule-conditions mb-2">
                                <li
                                    v-for="(part, idx) in card.parts"
                                    :key="idx"
                                    :class="{ 'text-danger': part.missing }"
                                >
                                    <i v-if="part.missing" class="bi bi-exclamation-triangle-fill mr-1"></i>
                                    {{ part.text }}
                                </li>
                                <li v-if="card.parts.length === 0" class="text-secondary">
                                    조건 없음 (모든 신청자)
                                </li>
                            </ul>

                            <div v-if="card.complex" class="text-secondary small mb-2">
                                <i class="bi bi-info-circle mr-1"></i>복잡한 규칙입니다. 자세한 편집은 고급 화면에서 하세요.
                            </div>
                        </template>

                        <div class="rule-card-actions d-flex flex-wrap">
                            <b-button size="sm" variant="outline-secondary" @click="$emit('edit', card.raw)">편집</b-button>
                            <b-button size="sm" variant="link" class="text-secondary p-0 ml-auto" @click="$emit('preview', card.raw)">
                                미리보기
                            </b-button>
                        </div>
                    </div>
                </b-col>
            </b-row>

            <!-- 규칙에 안 맞을 때 가는 기본 평가지 (읽기 전용 폴백) -->
            <div class="fallback-row d-flex align-items-center justify-content-between px-3 py-2 mt-1">
                <div>
                    <span class="text-secondary small mr-2">규칙에 안 맞을 때 →</span>
                    <span class="fw-500">기본 평가지:</span>
                    <span v-if="fallbackCampaignName" class="ml-1">{{ fallbackCampaignName }}</span>
                    <span v-else class="text-warning ml-1">
                        <i class="bi bi-exclamation-circle mr-1"></i>연결 안 됨
                    </span>
                </div>
                <b-badge variant="light" class="border">읽기 전용</b-badge>
            </div>
        </div>
    </b-card>
</template>

<script>
import {ruleGroupToParts} from './plain-language';
import PageEmptyComponent from '@shared/components/admin/components/common/PageEmptyComponent.vue';

export default {
    name: 'AssignmentRuleSection',
    components: {
        'p-page-empty-component': PageEmptyComponent,
    },
    props: {
        // 상세 조회된 규칙셋 목록. 각 항목에 root_rule_group(트리) + campaignName 이 채워져 있다.
        ruleSets: {type: Array, default: () => []},
        // 백엔드가 보고한 전체 규칙셋 수(표시분과 다를 수 있음).
        total: {type: Number, default: 0},
        // 안전 상한으로 일부만 가져왔는지(전체를 다 못 실었는지).
        truncated: {type: Boolean, default: false},
        // 질문 이름 → 라벨/선택지 라벨 매핑 (plain-language.buildKeyMap 결과). 라벨 변환 전용.
        keyMap: {type: Object, default: () => ({})},
        // 백엔드 readiness.missing_keys — 규칙이 참조하지만 신청서에 없는 질문(단일소스).
        missingKeys: {type: Array, default: () => []},
        // 규칙에 안 맞을 때 가는 기본 평가지 이름 (project.campaign)
        fallbackCampaignName: {type: String, default: null},
    },
    computed: {
        // 표시 수 라벨. 전체보다 적게 실었으면 "표시 N / 전체 M" 으로 명확히.
        countLabel() {
            if (this.total && this.total > this.ruleSets.length) {
                return `${this.ruleSets.length} 표시 / 전체 ${this.total}`;
            }
            return `${this.ruleSets.length}`;
        },
        // 규칙셋 원본을 카드 표시용으로 1회 변환 (prop 은 변이하지 않는다).
        cards() {
            const missingSet = new Set(this.missingKeys);
            return this.ruleSets.map((rs) => {
                const parsed = ruleGroupToParts(rs.root_rule_group, this.keyMap, missingSet);
                return {
                    id: rs.id,
                    campaign_id: rs.campaign_id,
                    campaignName: rs.campaignName,
                    is_active: rs.is_active,
                    // 상세(조건 트리) 조회 실패 여부 — ProjectConsole 이 표시.
                    detailError: !!rs.detailError,
                    parts: parsed.parts,
                    complex: parsed.complex,
                    raw: rs,
                };
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.console-section {
    border-radius: 8px;
}

.section-head {
    background: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
}

.section-index {
    font-weight: 700;
    color: #6c757d;
}

.rule-card {
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 0.9rem 1rem;
    background: #fff;
}

.rule-card--off {
    background: #f8f9fa;
    opacity: 0.85;
}

.rule-conditions {
    list-style: none;
    padding-left: 0;
    margin-bottom: 0;

    li {
        position: relative;
        padding-left: 1rem;
        font-size: 0.9rem;

        &::before {
            content: '·';
            position: absolute;
            left: 0.25rem;
        }
    }
}

.fallback-row {
    background: #f8f9fa;
    border: 1px dashed #ced4da;
    border-radius: 6px;
}

.gap-1 {
    gap: 0.25rem;
}
</style>
